import { map, filter, get, includes, isArray, mergeWith, first } from 'lodash';
import fs from 'fs-extra';
import path from 'upath';

import logger from '@bmd-studio/genstack-logger';
import environment from '@bmd-studio/genstack-environment';

import { LaunchConfig, LaunchConfiguration, LaunchCompound } from './index';
import { getServiceRepoRoot } from './services';
import { getMatchedContainers } from './docker';

const {
  DOCKER_CONTAINER_APP_PATH,
  DEFAULT_DEBUG_PORT,
} = environment.env;

const GENERATED_KEYWORD = '[generated]';

/**
 * Replace all the auto-generated config entries with the new ones reflected by the current services. 
 * This allows projects to still have their own launch entries while using this generator.
 * For example all node debugging ports are auto-discovered and added to the `launch.json` file.
 */
export const replaceVsCodeConfig = async (): Promise<void> => {
  const generatedLaunchConfig = await generateVsCodeLaunchConfig();
  const settingsRoot = environment.getProjectRoot(`.vscode`);
  const launchFileName = `launch.json`;
  const launchConfigPath = path.resolve(settingsRoot, launchFileName);
  let currentLaunchConfig = {};
  
  try {
    currentLaunchConfig = fs.readJsonSync(launchConfigPath) as LaunchConfig;

    logger.info.config(`${launchFileName} found! Cleaning all generated entries...`);
    currentLaunchConfig = cleanVsCodeLaunchConfig(currentLaunchConfig);
  } catch (error) {
    logger.info.config(`No ${launchFileName} found! Creating one...`);
  }

  // merge the cleaned and generated configurations to create the new one
  const newLaunchConfig = mergeWith(currentLaunchConfig, generatedLaunchConfig, (objValue, srcValue) => {
    if (isArray(objValue)) {
      return objValue.concat(srcValue);
    }
  });

  // write the new configuration
  logger.info.config(`Writing the new ${launchFileName} config to ${launchConfigPath}...`);
  logger.verbose.config(newLaunchConfig);
  fs.ensureDirSync(settingsRoot);
  fs.writeJsonSync(launchConfigPath, newLaunchConfig, { 
    flag: 'w', 
    spaces: 2 
  });
};

/**
 * Remove all entries with the `GENERATED_KEYWORD` in a specific property (e.g. name for configurations and compounds)
 * and return the remaining launch configuration.
 * @param launchConfig 
 */
export const cleanVsCodeLaunchConfig = (launchConfig: LaunchConfig): LaunchConfig => {
  const cleanEntries = <T>(entries: T[] | undefined, path: string): T[] | undefined => {
    return filter(entries, (entry) => {
      const pathValue = get(entry, path);

      // guard: check if the requested value contains the generated keyword
      // this means we will filter the entry out
      if (includes(pathValue, GENERATED_KEYWORD)) {
        return false;
      }

      return true;
    });
  };
  const cleanVsCodeLaunchConfig = {
    ...launchConfig,
    configurations: cleanEntries<LaunchConfiguration>(launchConfig.configurations, `name`),
    compounds: cleanEntries<LaunchCompound>(launchConfig.compounds, `name`),
  };

  return cleanVsCodeLaunchConfig;
};

/**
 * Generate VS Code launch.json configuration entries for the currently running services to provide 
 * them with debugging capabilities.
 */
export const generateVsCodeLaunchConfig = async (): Promise<LaunchConfig> => {
  const matchedContainers = await getMatchedContainers();
  const configurations: LaunchConfiguration[] = []; 
  const compounds: LaunchCompound[] = [];
  const debugPortKey = `${DEFAULT_DEBUG_PORT}/tcp`;

  map(matchedContainers, (matchedContainer) => {
    const container = matchedContainer.container;
    const service = matchedContainer.service;
    const { name: serviceName = '', repository: serviceRepository, path: servicePath = '' } = service ?? {};
    const pathCandidates = [{
      isValid: !!serviceRepository,
      localRoot: service ? getServiceRepoRoot(service) : '',
      remoteRoot: DOCKER_CONTAINER_APP_PATH,
    }, {
      isValid: servicePath && !serviceRepository,
      localRoot: environment.getProjectRoot(servicePath),
      remoteRoot: DOCKER_CONTAINER_APP_PATH,
    }];
    let hostDebugPort: number = 0;

    map(container.NetworkSettings.Ports, (hostAddress, containerPortKey) => {
      if (containerPortKey !== debugPortKey) {
        return;
      }

      hostDebugPort = parseInt(first(hostAddress)?.HostPort ?? '0');
    });

    // guard: check if a debug port is found
    if (!hostDebugPort) {
      return undefined;
    }

    map(pathCandidates, (pathCandidate) => {
      const { isValid, localRoot, remoteRoot } = pathCandidate;

      if (!isValid || !localRoot || !remoteRoot) {
        return;
      }

      configurations.push(generateDebugNodeLaunchEntry(
        serviceName, 
        hostDebugPort,
        localRoot,
        remoteRoot
      ));
    });
  });

  compounds.push({
    name: "Debug All [generated]",
    configurations: map(configurations, (configuration) => {
      return configuration.name;
    }),
  });

  const launchConfig: LaunchConfig = {
    configurations,
    compounds,
  };

  return launchConfig;
};

/**
 * Generate a single VS Code launch.json entry for debugging node processes.
 * @param sourceLocation Postfix of where the source is located as it might be distributed across packages
 * @param serviceName Name of the service
 * @param port Port where the debugger is running on
 * @param localRoot Host machine directory of where the source is located
 */
export const generateDebugNodeLaunchEntry = (serviceName: string, port: number, localRoot: string, remoteRoot: string): LaunchConfiguration => {
  return {
    type: 'node',
    request: 'attach',
    name: `Debug ${serviceName} ${GENERATED_KEYWORD}`,
    protocol: 'auto',
    port: port,
    restart: true,
    sourceMaps: true,
    localRoot: `${localRoot}`,
    remoteRoot: `${remoteRoot}`,
    outFiles: [
      path.join(localRoot, `**/*.js`),
    ],
    skipFiles: [
      `<node_internals>/**/*.js`,
    ],
  };
};